import React, { useEffect, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { FieldCropDetection, FieldCropDetectionStatus, getFieldDetections, uploadFieldsExperiment } from "services";

import {
  SHAPEFILE_VALIDATION_ERRORS,
  addShapeProcessor,
  checkValidShapefileSize,
  checkValidShapefileType,
  getEnvironmentString,
  handleFileUpload,
  useAppType,
  useFeatureFlag,
  useGetFarms,
  useTranslation,
} from "utils";

import { BNP_CARBON_PROGRAM_ID, FEATURE_FLAGS, GET_FIELDS } from "consts";

import {
  Button,
  Checkbox,
  Col,
  Dragger,
  ExtraInfo,
  Flex,
  Form,
  Icon,
  LazyLoadImage,
  Link,
  LinkMoreInfo,
  Row,
  ScreenSuccess,
  Spin,
  StickyFooterButtons,
  Typography,
  Message as message,
} from "components";

import { CropDetectionModal } from "pages/app/fields/components/CropDetectionModal";
import { FieldDetails } from "pages/app/fields/components/FieldDetails";

const SHAPEFILE_INFO_TYPE = "shapefile-upload-guidance";
const DETECTION_POLL_INTERVAL_MS = 10000;
const MAX_POLL_DURATION_MS = 6 * 60 * 1000; // 6 min — confirmed detection takes ~5–6 min end-to-end
const TERMINAL_STATUSES = new Set<FieldCropDetectionStatus>(["completed", "failed", "skipped"]);

const extractYearRange = (detections: FieldCropDetection[]) => {
  const years = detections.map((d) => d.seasonYear).filter(Boolean);
  if (years.length === 0) return null;
  return { startYear: Math.min(...years), endYear: Math.max(...years) };
};

export type FieldUploadStep = "INTRO" | "UPLOAD" | "MANUAL_UPLOAD" | "SUCCESS";

type FieldUploadProps = {
  onFinish: () => void;
  initialStep?: FieldUploadStep;
};

export const FieldUpload = ({ onFinish, initialStep = "INTRO" }: FieldUploadProps) => {
  const { t, ready } = useTranslation(["onboarding", "enums"], { useSuspense: false });
  const { mobile } = useAppType();
  const queryClient = useQueryClient();
  const env = getEnvironmentString();
  const { currentFarm } = useGetFarms();
  const { isEnabled: isSoilSightEnabled } = useFeatureFlag(FEATURE_FLAGS.SOIL_SIGHT_INTEGRATION);

  const defaultTriggerCropDetection = isSoilSightEnabled && currentFarm?.carbonProgram?.id === BNP_CARBON_PROGRAM_ID;

  const [fieldUploadStep, setFieldUploadStep] = useState<FieldUploadStep>(initialStep);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggleModuleExplainerModal, setToggleModuleExplainerModal] = useState(false);
  const [upload, setUpload] = useState("");
  const [uploadURL, setUploadURL] = useState("");
  const [uploadPercent, setUploadPercent] = useState(0);
  const [showShapefileInfoDrawer, setShowShapefileInfoDrawer] = useState(false);
  const [detectionYearRange, setDetectionYearRange] = useState<{ startYear: number; endYear: number } | null>(null);
  const [triggerCropDetection, setTriggerCropDetection] = useState(defaultTriggerCropDetection);

  const yearsOfCropDetections = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - 4 + i);
  }, []);

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fieldIdsRef = useRef<string[]>([]);
  const pendingFieldIdsRef = useRef<string[]>([]);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  };

  const handleDetectionsDone = () => {
    stopPolling();
    setUploadPercent(100);
    queryClient.invalidateQueries({ queryKey: [GET_FIELDS] });
  };

  const pollDetections = async () => {
    try {
      const detections = (await Promise.all(pendingFieldIdsRef.current.map(getFieldDetections))).flat();

      if (detections.length === 0) {
        handleDetectionsDone();
        return;
      }

      const yearRange = extractYearRange(detections);
      if (yearRange) setDetectionYearRange(yearRange);

      // Remove fields whose detections are all terminal — no need to poll them again
      pendingFieldIdsRef.current = pendingFieldIdsRef.current.filter((id) => {
        const fieldDets = detections.filter((d) => d.fieldId === id);
        return fieldDets.length === 0 || !fieldDets.every((d) => TERMINAL_STATUSES.has(d.status));
      });

      if (pendingFieldIdsRef.current.length === 0) {
        handleDetectionsDone();
        return;
      }

      const totalFields = fieldIdsRef.current.length;
      const doneFields = totalFields - pendingFieldIdsRef.current.length;
      setUploadPercent(Math.min(Math.floor(10 + (doneFields / totalFields) * 89), 99));
    } catch {
      // Polling error — keep retrying
    }
  };

  const SIMPLE_FORMAT_ISSUES = new Set([
    SHAPEFILE_VALIDATION_ERRORS.INVALID_EXTENSION,
    SHAPEFILE_VALIDATION_ERRORS.INVALID_SIGNATURE,
    SHAPEFILE_VALIDATION_ERRORS.MISSING_REQUIRED_FILE,
    SHAPEFILE_VALIDATION_ERRORS.NESTED_ZIP,
  ]);

  const getFormattedErrorMessage = (reason?: keyof typeof SHAPEFILE_VALIDATION_ERRORS, meta?: string) => {
    switch (reason) {
      case SHAPEFILE_VALIDATION_ERRORS.NESTED_ZIP:
        return t("onboarding:uploadFileNestedZip");
      case SHAPEFILE_VALIDATION_ERRORS.MISSING_REQUIRED_FILE:
        return t("onboarding:uploadFileInvalidStructure", { fileType: meta?.toLowerCase() });
      case SHAPEFILE_VALIDATION_ERRORS.INVALID_SIGNATURE:
        return t("onboarding:uploadFileInvalidSignature");
      case SHAPEFILE_VALIDATION_ERRORS.INVALID_EXTENSION:
        return t("onboarding:uploadFileInvalidExtension");
      default:
        return t("onboarding:uploadFileWrongArchive");
    }
  };

  const handleFormatIssue = (reason?: string, meta?: string) => {
    const shouldOpenDrawer = reason && reason !== SHAPEFILE_VALIDATION_ERRORS.NESTED_ZIP && SIMPLE_FORMAT_ISSUES.has(reason ?? "");
    message({
      content: getFormattedErrorMessage(reason as keyof typeof SHAPEFILE_VALIDATION_ERRORS, meta),
      icon: "error",
      onClose: () => {
        if (shouldOpenDrawer) setShowShapefileInfoDrawer(true);
      },
    });
  };

  const handleUpload = (file: File) => {
    setSaving(true);
    handleFileUpload({ file, location: "test-shapefiles" })
      .then((res) => {
        setUploadURL(res.location);
        setUpload("success");
        message({ content: t("uploadFileSuccess"), icon: "tick" });
      })
      .finally(() => {
        setLoading(false);
        setSaving(false);
      });
  };

  const fileUpload = async (file: File) => {
    const typeValidation = await checkValidShapefileType(file);
    const isValidSize = await checkValidShapefileSize(file);

    if (!typeValidation.isValid) {
      handleFormatIssue(typeValidation.reason, typeValidation.meta ?? undefined);
      return false;
    }

    if (!isValidSize) {
      message({ content: t("invekosTooLarge"), icon: "error" });
      return false;
    }

    handleUpload(file);
    return false;
  };

  const complete = async () => {
    setSaving(true);
    setUpload("inProgress");
    setUploadPercent(0);
    setDetectionYearRange(null);

    try {
      const createdFields = await uploadFieldsExperiment({ file: uploadURL, triggerRemoteSensing: triggerCropDetection, seasonYears: yearsOfCropDetections });
      const fieldIds = createdFields.map((f) => f.id);
      fieldIdsRef.current = fieldIds;
      pendingFieldIdsRef.current = [...fieldIds];
      setUploadPercent(10);

      if (triggerCropDetection && fieldIds.length > 0) {
        pollingIntervalRef.current = setInterval(pollDetections, DETECTION_POLL_INTERVAL_MS);
        pollingTimeoutRef.current = setTimeout(handleDetectionsDone, MAX_POLL_DURATION_MS);
      } else {
        handleDetectionsDone();
        onFinish();
      }
    } catch {
      setUpload("failed");
    } finally {
      setSaving(false);
    }
  };

  const incomplete = () => {
    message({ content: t("errors.mandatoryFields"), icon: "error" });
  };

  const handleModalDone = async () => {
    stopPolling();
    await queryClient.refetchQueries({ queryKey: [GET_FIELDS] });
    onFinish();
  };

  useEffect(() => {
    addShapeProcessor();
    return stopPolling;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    !loading &&
    ready && (
      <section className="onboarding">
        {triggerCropDetection && (
          <CropDetectionModal
            isVisible={upload === "inProgress"}
            progress={uploadPercent}
            startYear={detectionYearRange?.startYear}
            endYear={detectionYearRange?.endYear}
            onDone={handleModalDone}
          />
        )}
        {fieldUploadStep === "INTRO" && (
          <>
            <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 0]} dataTestId="onboarding-field-info">
              <Col className="margin-bottom-100">
                <LazyLoadImage alt="farm-info-photo" src={"/images/onboarding/field.jpg"} width={"100%"} />
              </Col>
              <Col className="padding-bottom-100">
                <Typography variant="h1" className={mobile ? "margin-bottom-200" : "margin-bottom-100"}>
                  {t("onboarding:onboarding.addFarmAndField.fieldTitle")}
                </Typography>
                <Typography variant="paragraph-m" className={mobile ? "margin-bottom-100" : "margin-bottom-225"}>
                  {t("onboarding:onboarding.addFarmAndField.fieldDescription")}
                </Typography>
                <LinkMoreInfo dataTestId="field-upload-auto-more-info-link" onClick={() => setShowShapefileInfoDrawer(!showShapefileInfoDrawer)}>
                  {t("onboarding:invekosPopup")}
                </LinkMoreInfo>
                <Button
                  block
                  dataTestId="onboarding-step1-field-add-auto"
                  className={mobile ? "margin-top-400" : "margin-top-225"}
                  type="primary"
                  onClick={() => setFieldUploadStep("UPLOAD")}
                >
                  {t("onboarding:fieldDetails.onboardingButton.addFieldsAuto")}
                </Button>
                <Button
                  block
                  dataTestId="onboarding-step1-field-add-manually"
                  className="margin-top-100"
                  type={"secondary"}
                  onClick={() => setFieldUploadStep("MANUAL_UPLOAD")}
                >
                  {t("onboarding:fieldDetails.onboardingButton.addFieldsManually")}
                </Button>
              </Col>
            </Row>
          </>
        )}
        {fieldUploadStep === "MANUAL_UPLOAD" && <FieldDetails onFinish={onFinish} openMapOnMount={initialStep === "MANUAL_UPLOAD"} />}
        {fieldUploadStep === "UPLOAD" && (
          <Form name="upload" onFinish={complete} onFinishFailed={incomplete}>
            <section className="container tiny">
              <Row className="margin-bottom-350">
                <Col>
                  <Typography variant="h1">{t("uploadInvekosHeadline")}</Typography>
                </Col>
                <Col>
                  <Typography variant="paragraph-m">{t("uploadInvekosDescription")}</Typography>
                  <LinkMoreInfo onClick={() => setShowShapefileInfoDrawer(!showShapefileInfoDrawer)}>{t("invekosPopup")}</LinkMoreInfo>
                </Col>
              </Row>
            </section>
            <section className="container tiniest">
              <Row>
                <Col>
                  <Form.Item name="file" rules={[{ required: true }]} className="center">
                    <Dragger
                      name="file"
                      showUploadList={false}
                      action=""
                      beforeUpload={(v) => fileUpload(v)}
                      className={upload === "success" ? "success" : upload === "failed" ? "failed" : ""}
                    >
                      {!upload && (
                        <Flex align="center" justify="center" gap={16}>
                          <Icon iconName="klim/plus" size="xl" />
                          <Typography variant="paragraph-m">{t("uploadFile")}</Typography>
                        </Flex>
                      )}
                      {upload === "inProgress" && (
                        <>
                          <Spin />
                          <Typography variant="paragraph-m" className="center in-progress">
                            {uploadPercent} %
                          </Typography>
                          <div className="loading-outer">
                            <div className="loading-inner" style={{ width: `${uploadPercent}%` }} />
                          </div>
                        </>
                      )}
                      {upload === "success" && (
                        <>
                          <Icon iconName="tabler/file-check" size="xl" />
                          <Typography variant="paragraph-m">{t("uploadFileSuccess")}</Typography>
                        </>
                      )}
                      {upload === "failed" && (
                        <>
                          <Icon iconName="tabler/file-alert" size="xl" />
                          <Typography variant="paragraph-m">{t("uploadFileFailed")}</Typography>
                        </>
                      )}
                    </Dragger>
                  </Form.Item>
                </Col>
              </Row>
            </section>
            {env !== "production" && (
              <Checkbox className="margin-top-100" checked={triggerCropDetection} onChange={(e) => setTriggerCropDetection(e.target.checked)}>
                Trigger crop detection
              </Checkbox>
            )}
            <StickyFooterButtons handleBack={() => setFieldUploadStep("INTRO")} absolute>
              <Button type="primary" htmlType="submit" disabled={upload === "inProgress" || saving} loading={saving}>
                {t("uploadDocumentButton")}
              </Button>
            </StickyFooterButtons>
          </Form>
        )}
        {fieldUploadStep === "SUCCESS" && (
          <ScreenSuccess
            description={t("invekosUploadSuccessDescription")}
            headline={t("invekosUploadSuccessHeadline")}
            infoLinkText={t("invekosUploadSuccessInfoLink")}
            onInfoLinkClick={() => setToggleModuleExplainerModal(!toggleModuleExplainerModal)}
          >
            <Row justify="center" align="middle">
              <Col>
                <Button type="primary" loading={saving} onClick={onFinish} mixpanelText={t("invekosUploadSuccessButton")}>
                  {t("invekosUploadSuccessButton")}
                </Button>
              </Col>
            </Row>
            <Row justify="center" align="middle">
              <Col>
                <Link onClick={onFinish} mixpanelText={t("invekosUploadSkipButton")}>
                  {t("invekosUploadSkipButton")}
                </Link>
              </Col>
            </Row>
          </ScreenSuccess>
        )}
        <ExtraInfo type={SHAPEFILE_INFO_TYPE} visible={showShapefileInfoDrawer} setVisible={setShowShapefileInfoDrawer} />
      </section>
    )
  );
};
